import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { removeMetaFields } from "@/lib/removeMetaFields";
import {
  DropdownService,
  DynamicModuleService,
  SchemaService,
} from "@/services/APIs";
import { ProColumns } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Popconfirm } from "antd";
import { Edit2, Plus, Trash, UserCog } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const DESIGNATION_MASTER_SCHEMA_NAME = "PTS Designation Master";
const DESIGNATION_SCHEMA_NAME = "PTS Designation";

function getRecordId(record: any): string {
  if (!record || typeof record !== "object") {
    return "";
  }

  const value =
    record.id ?? record.ID ?? record.Id ?? record._id ?? record.pk ?? "";

  return value !== null ? String(value).trim() : "";
}

const hasSchema = (
  json: Record<string, any> | string | null | undefined
): json is Record<string, any> => {
  return (
    typeof json !== "string" &&
    !!json &&
    typeof json === "object" &&
    "schema" in json
  );
};

const hasXComponent = (
  property: any
): property is { "x-component": string; title: string } => {
  return (
    property &&
    typeof property === "object" &&
    typeof property["x-component"] === "string" &&
    typeof property.title === "string"
  );
};

function getFirstPresentValue(
  row: Record<string, unknown>,
  keys: string[]
): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return undefined;
}

const DesignationMasterPage = () => {
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingDesignationMasterId, setEditingDesignationMasterId] = useState<
    string | null
  >(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const form = useMemo(() => createForm({}), []);

  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", DESIGNATION_MASTER_SCHEMA_NAME],
    queryFn: () =>
      SchemaService.getSchemaByName(DESIGNATION_MASTER_SCHEMA_NAME),
    enabled: true,
  });

  const { data: designationMasterData, isLoading: designationMasterLoading } =
    useQuery({
      queryKey: [
        "designation-master-data",
        DESIGNATION_MASTER_SCHEMA_NAME,
        editingDesignationMasterId,
      ],
      queryFn: () => {
        if (!editingDesignationMasterId) {
          throw new Error("Designation Master ID is required");
        }

        return DynamicModuleService.getItemById(
          DESIGNATION_MASTER_SCHEMA_NAME,
          editingDesignationMasterId
        );
      },
      enabled: !!editingDesignationMasterId,
    });

  useEffect(() => {
    if (isEditMode && designationMasterData) {
      form.setValues(removeMetaFields(designationMasterData));
    } else if (!isEditMode) {
      form.reset();
    }
  }, [designationMasterData, form, isEditMode]);

  const { data: designationOptions } = useQuery({
    queryKey: ["dropdown", DESIGNATION_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(DESIGNATION_SCHEMA_NAME),
  });

  const designationNameMap = useMemo(() => {
    const map = new Map<string, string>();

    (designationOptions ?? []).forEach((option: any) => {
      const value = option?.value;
      const label = option?.label;
      if (value !== undefined && value !== null && label) {
        map.set(String(value), String(label));
      }
    });

    return map;
  }, [designationOptions]);

  const getDesignationDisplayName = useCallback(
    (record: Record<string, unknown>) => {
      const rawValue = getFirstPresentValue(record, [
        "designation",
        "designation_id",
        "pts_designation",
        "pts_designation_id",
      ]);

      if (rawValue === undefined || rawValue === null || rawValue === "") {
        return "-";
      }

      if (typeof rawValue === "object") {
        const objectValue = (rawValue as any)?.id ?? (rawValue as any)?.value;
        const objectLabel = (rawValue as any)?.label ?? (rawValue as any)?.name;

        if (objectLabel) {
          return String(objectLabel);
        }

        if (objectValue !== undefined && objectValue !== null) {
          return (
            designationNameMap.get(String(objectValue)) ?? String(objectValue)
          );
        }
      }

      return designationNameMap.get(String(rawValue)) ?? String(rawValue);
    },
    [designationNameMap]
  );

  const getEstimatePriceDisplayValue = (record: Record<string, unknown>) => {
    const rawValue = getFirstPresentValue(record, [
      "estimateprice",
      "estimate_price",
      "estimated_price",
      "price",
      "rate",
      "amount",
    ]);

    if (rawValue === undefined || rawValue === null || rawValue === "") {
      return "-";
    }

    return String(rawValue);
  };

  const refreshDesignationMasterList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["moduleData", DESIGNATION_MASTER_SCHEMA_NAME],
    });
  }, [queryClient]);

  const handleEdit = (id: string | undefined) => {
    if (!id) {
      notify.error("Designation Master ID is missing");
      return;
    }

    setEditingDesignationMasterId(String(id));
    setIsEditMode(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingDesignationMasterId(null);
    form.reset();
  };

  const handleDelete = useCallback(
    async (id: string | undefined) => {
      if (!id) {
        notify.error("Designation Master ID is missing");
        return;
      }

      try {
        await DynamicModuleService.deleteItem(
          DESIGNATION_MASTER_SCHEMA_NAME,
          String(id)
        );
        notify.success("Designation master deleted successfully");
        refreshDesignationMasterList();
      } catch (error: any) {
        notify.error(error?.message ?? "Failed to delete designation master");
      }
    },
    [refreshDesignationMasterList]
  );

  const handleSubmitDesignationMaster = async () => {
    try {
      await form.validate();
      let modifiedValues = removeMetaFields(form.values);
      setIsSubmitting(true);

      if (schemaInfo?.json && hasSchema(schemaInfo.json)) {
        const properties = schemaInfo.json.schema?.properties ?? {};

        Object.values(properties).forEach((property: any) => {
          if (
            hasXComponent(property) &&
            property["x-component"] === "CustomUpload"
          ) {
            const value = modifiedValues[property.title];
            if (Array.isArray(value) && value.length > 0 && value[0]?.id) {
              modifiedValues = {
                ...modifiedValues,
                [property.title]: value[0].id,
              };
            }
          }

          if (
            hasXComponent(property) &&
            property["x-component"] === "CustomImageUpload"
          ) {
            const value = modifiedValues[property.title];
            if (value && typeof value === "object" && "id" in value) {
              modifiedValues = {
                ...modifiedValues,
                [property.title]: value.id,
              };
            }
          }
        });
      }

      if (isEditMode && editingDesignationMasterId) {
        if (!checkFormChanges(modifiedValues, designationMasterData, notify)) {
          setIsSubmitting(false);
          return;
        }

        const response = await DynamicModuleService.updateItem(
          DESIGNATION_MASTER_SCHEMA_NAME,
          editingDesignationMasterId,
          modifiedValues
        );

        if (response) {
          handleCancelEdit();
          refreshDesignationMasterList();
        }
      } else {
        const response = await DynamicModuleService.createItem(
          DESIGNATION_MASTER_SCHEMA_NAME,
          modifiedValues
        );

        if (response) {
          form.reset();
          refreshDesignationMasterList();
        }
      }
    } catch (error: any) {
      notify.error(
        error?.message ??
          `Failed to ${isEditMode ? "update" : "create"} designation master`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = selectedRowKeys.map(key => String(key)).filter(Boolean);

    if (!ids.length) {
      notify.error("Please select at least one record to delete");
      return;
    }

    try {
      await DynamicModuleService.bulkDeleteItems(
        DESIGNATION_MASTER_SCHEMA_NAME,
        ids
      );
      notify.success(
        ids.length === 1
          ? "Designation master deleted successfully"
          : `${ids.length} designation master records deleted successfully`
      );
      setSelectedRowKeys([]);
      refreshDesignationMasterList();
    } catch (error: any) {
      notify.error(error?.message ?? "Failed to delete selected records");
    }
  };

  const columns: ProColumns<any>[] = useMemo(() => {
    return [
      {
        title: "Designation",
        dataIndex: "designation",
        key: "designation",
        render: (_: any, record: any) => (
          <span className="text-sm text-gray-800">
            {getDesignationDisplayName(record)}
          </span>
        ),
      },
      {
        title: "Estimate Price",
        dataIndex: "estimateprice",
        key: "estimateprice",
        render: (_: any, record: any) => (
          <span className="text-sm text-gray-800">
            {getEstimatePriceDisplayValue(record)}
          </span>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 120,
        align: "center" as const,
        render: (_: any, record: any) => {
          const recordId = getRecordId(record);

          return [
            <CustomButton
              key="edit"
              icon={<Edit2 size={16} />}
              type="text"
              title="Edit designation master"
              onClick={() => handleEdit(recordId)}
              disabled={!recordId}
            />,
            <Popconfirm
              key="delete"
              title="Are you sure you want to delete this designation master?"
              onConfirm={() => handleDelete(recordId)}
              okText="Yes"
              cancelText="No"
              disabled={!recordId}
            >
              <CustomButton
                key="delete-button"
                icon={<Trash size={16} />}
                type="text"
                danger
                title="Delete designation master"
                disabled={!recordId}
              />
            </Popconfirm>,
          ];
        },
      },
    ];
  }, [getDesignationDisplayName, handleDelete]);

  const isLoading = schemaLoading || (isEditMode && designationMasterLoading);

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Designation Master
          </h1>
          <p className="text-sm text-[#627384]">
            Manage designation master records and estimated rates
          </p>
        </div>
      </div>

      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-4 md:p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-[#111827]">
            {isEditMode ? (
              <>
                <Edit2 className="h-5 w-5" />
                Edit Designation Master
              </>
            ) : (
              <>
                <Plus className="h-5 w-5" />
                Create Designation Master
              </>
            )}
          </h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="mb-4">
                <SchemaFormView
                  key={`form-${isEditMode ? "edit" : "create"}-${editingDesignationMasterId ?? "new"}`}
                  form={form}
                  schemaJson={schemaInfo?.json ?? schemaInfo}
                />
              </div>

              <div className="flex justify-end gap-3">
                {isEditMode && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={handleSubmitDesignationMaster}
                  disabled={isSubmitting || isLoading}
                  className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
                >
                  {isEditMode ? (
                    <>
                      <Edit2 className="h-4 w-4" />
                      {isSubmitting
                        ? "Updating..."
                        : "Update Designation Master"}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      {isSubmitting
                        ? "Creating..."
                        : "Create Designation Master"}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-4">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-[#111827]">
                Designation Master List
              </h2>
            </div>

            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title="Delete selected designation master records?"
                onConfirm={handleBulkDelete}
                okText="Yes"
                cancelText="No"
              >
                <CustomButton danger icon={<Trash size={16} />}>
                  Delete Selected
                </CustomButton>
              </Popconfirm>
            )}
          </div>

          <ConfigDynamicTable<any>
            tableName={DESIGNATION_MASTER_SCHEMA_NAME}
            columns={columns}
            headerTitle=""
            rowKey={(record: any) => getRecordId(record)}
            filter={{ ordering: "-created_on" }}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default DesignationMasterPage;
